import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toastAlert } from "../../helpers/toastAlert";

/**
 * Admin-only password reset for an employee or customer.
 *
 * Step 1 asks for confirmation; step 2 shows the generated temporary password
 * once (it is not stored anywhere readable) so the admin can share it.
 *
 * `onReset(id)` must call the reset API and resolve to the response body
 * ({ code, data: { temporaryPassword, emailed, email }, message }) or an error.
 */
const ResetPasswordModal = ({ open, onClose, target, onReset }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setLoading(false);
      setResult(null);
    }
  }, [open, target?.id]);

  const handleReset = async () => {
    setLoading(true);
    const res = await onReset(target.id);
    setLoading(false);
    if (res?.data?.temporaryPassword) {
      setResult(res.data);
    } else {
      toastAlert(
        res?.response?.data?.message || "Failed to reset password",
        "error",
      );
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      toastAlert("Temporary password copied", "success");
    } catch (_) {
      toastAlert("Copy failed - please select and copy it manually", "error");
    }
  };

  return (
    <Modal
      open={open}
      onClose={!loading ? onClose : undefined}
      disableEnforceFocus
      disableAutoFocus
      sx={{ zIndex: 40000 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(500px, calc(100vw - 32px))",
          bgcolor: "#fff",
          boxShadow: 24,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "var(--thm-color)",
            padding: "15px",
            borderRadius: "10px 10px 0px 0px",
            marginBottom: "15px",
          }}
        >
          <LockResetIcon sx={{ fontSize: 32, color: "#e0ddddff" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#e0ddddff" }}>
            Reset Password
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 2 }}>
          {!result ? (
            <Typography sx={{ color: "text.secondary" }}>
              Reset the password for <strong>{target?.name || "this user"}</strong>?
              A new temporary password will be generated
              {target?.email ? ` and emailed to ${target.email}` : ""}. Their
              current password will stop working.
            </Typography>
          ) : (
            <>
              <Typography sx={{ color: "text.secondary", mb: 1.5 }}>
                Share this temporary password with{" "}
                <strong>{target?.name || "the user"}</strong>. It is shown only
                once. Ask them to change it after signing in.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{ fontFamily: "monospace", fontSize: 18, userSelect: "all" }}
                >
                  {result.temporaryPassword}
                </Typography>
                <IconButton title="Copy" onClick={copyPassword}>
                  <ContentCopyIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {result.emailed
                  ? `Also emailed to ${result.email}.`
                  : result.email
                    ? `Email to ${result.email} could not be sent - share it directly.`
                    : "No email on file - share it directly."}
              </Typography>
            </>
          )}
        </Box>

        <Divider />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
          {!result ? (
            <>
              <Button className="btn btn-border" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button className="btn btn-colored" onClick={handleReset} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : "Reset password"}
              </Button>
            </>
          ) : (
            <Button className="btn btn-colored" onClick={onClose}>
              Done
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ResetPasswordModal;
